import api from '@/redux/api/catalogDetail';
import cloneDeep from 'lodash/cloneDeep';
import { createSelector } from 'reselect';
import difference from 'lodash/difference';
import { getDeployment } from './config';
import { GlobalState } from '../rootReducer';
import ms from 'ms';
import union from 'lodash/union';

/* Action Types */
export const LOAD_CATALOG_DETAILS_FAIL = 'la/domain/catalogDetail/LOAD_FAIL';
export const LOAD_CATALOG_DETAILS_REQUEST = 'la/domain/catalogDetail/LOAD_REQUEST';
export const LOAD_CATALOG_DETAILS_SUCCESS = 'la/domain/catalogDetail/LOAD_SUCCESS';

const REDUX_STORE_TIME = ms('30m');

const splitCustomTerms = (terms) => {
    const paymentDetails = terms.find((term) => term.id === 'pay') || {};
    const shippingDetails = terms.find((term) => term.id === 'ship') || {};
    const customTerms = terms.filter((term) => term.id !== 'pay' && term.id !== 'ship');

    return {
        customTerms,
        paymentDetails: paymentDetails.data || '',
        shippingDetails: shippingDetails.data || '',
    };
};

/* reducer */
const DEFAULT_STATE = {
    byId: {},
    loaded: {},
    loading: [],
};

export type State = typeof DEFAULT_STATE;

export default function reducer(state: State = DEFAULT_STATE, action: any = {}) {
    let existing;
    let loaded;
    let loading;
    let time;

    switch (action.type) {
        case LOAD_CATALOG_DETAILS_FAIL:
            return {
                ...state,
                loading: difference(state.loading, action.meta.catalogIds),
            };
        case LOAD_CATALOG_DETAILS_REQUEST:
            return {
                ...state,
                loading: union(state.loading, action.payload),
            };
        case LOAD_CATALOG_DETAILS_SUCCESS:
            existing = cloneDeep(state.byId);
            loaded = cloneDeep(state.loaded);
            loading = cloneDeep(state.loading);
            time = action.meta.actionTime;

            if (action.payload.catalogDetails) {
                action.payload.catalogDetails.forEach((item) => {
                    existing[item.catalogId] = {
                        catalogId: item.catalogId,
                        externalUrl: item.externalUrl,
                        freeLocalPickup: item.freeLocalPickup,
                        hasWeapons: item.hasWeapons,
                        inHouseShipping: item.inHouseShipping,
                        ...splitCustomTerms(item.customTerms),
                        streamId: item.streamId || 0,
                        terms: item.terms,
                        videoSource: item.videoSource || '',
                    };
                    loaded[item.catalogId] = time;
                    loading = difference(loading, [item.catalogId]);
                });
            }

            return {
                ...state,
                byId: existing,
                loaded,
                loading,
            };
        default:
            return state;
    }
}

/* SELECTORS */
const stateSelector = (state: GlobalState) => state.catalogDetail;
const idSelector = (state: GlobalState, id) => id;

const byIdSelector = createSelector(stateSelector, (state) => state.byId);

const loadedSelector = createSelector(stateSelector, (state) => state.loaded);

const loadingSelector = createSelector(stateSelector, (state) => state.loading);

export const getCatalogDetail: (state: any, selector: any) => any = createSelector(
    [byIdSelector, idSelector],
    (byId, id) => byId[id] || {}
);

const getLoadTimeForCatalogDetail = createSelector([loadedSelector, idSelector], (loaded, id) => loaded[id] || 0);

const isCatalogDetailLoading = createSelector([loadingSelector, idSelector], (loading, id) => loading.includes(id));

const shouldFetchCatalogDetail = (state, catalogId) => {
    if (!catalogId) {
        return false;
    }
    const item = getCatalogDetail(state, catalogId);
    if (item) {
        const loaded = getLoadTimeForCatalogDetail(state, catalogId);
        const time = Date.now();
        const diff = time - loaded;
        if (diff < REDUX_STORE_TIME) {
            return false;
        }
    }
    const loading = isCatalogDetailLoading(state, catalogId);
    return !loading;
};

const whichCatalogDetailsNeeded = (state, catalogIds) =>
    catalogIds.filter((catalogId) => shouldFetchCatalogDetail(state, catalogId));

/* ACTION CREATORS */
const loadCatalogDetails = (catalogIds) => async (dispatch, getState) => {
    try {
        const state = getState();
        const deployment = getDeployment(state);
        dispatch({
            payload: catalogIds,
            type: LOAD_CATALOG_DETAILS_REQUEST,
        });
        const response = await api.fetchCatalogDetailsByIds({
            catalogIds,
            deployment,
        });
        dispatch({
            meta: { actionTime: Date.now(), catalogIds },
            payload: response.data,
            type: LOAD_CATALOG_DETAILS_SUCCESS,
        });
    } catch (error) {
        dispatch({
            error: true,
            meta: { catalogIds },
            payload: error,
            type: LOAD_CATALOG_DETAILS_FAIL,
        });
    }
};

export const fetchCatalogDetailsIfNeeded = (catalogIds: number[]) => async (dispatch: Function, getState: Function) => {
    const needed = whichCatalogDetailsNeeded(getState(), catalogIds);
    if (needed.length) {
        return dispatch(loadCatalogDetails(needed));
    }
    return Promise.resolve();
};
